/**
 * Where a carried thing can land, and how a point on the screen becomes one.
 *
 * DOM things (orbs, gates, rows, the field's cells) register an element and
 * a function that says what target it is right now; `resolve` finds them with
 * `elementFromPoint` + `closest('[data-target]')`, which needs no coordinate
 * maths at any zoom. Things drawn only in WebGL (bits of rows the DOM does not
 * render) are found by camera maths: the viewport point goes back through the
 * camera to stage units, and from there to a row and a column (`bitAt`).
 *
 * The registry also keeps the scrub state of each World: the River sets it,
 * the field reads it, and a write dropped on a scrubbed World returns it to
 * Live first (§5.5 rule 9).
 */

import type { Camera, FieldLayout, Held, PlaneId, Region, Target, Unsub, WorldMatter } from '../contracts';

// ---------- camera maths for bits ----------

export interface BitHit { readonly key: number; readonly slot: number; readonly plane?: PlaneId }

/**
 * The bit at a stage point, or null in a gap or off the field. A row's or a
 * lane's y is the top of its bits as an offset from the layout's y (the
 * contract's FieldLayout, as the renderers read it); a lane is a plane's own
 * strip in the exploded view.
 */
export const bitAt = (layout: FieldLayout, p: { readonly x: number; readonly y: number }): BitHit | null => {
  const pitch = layout.cell + layout.gap;
  if (pitch <= 0) return null;
  const dx = p.x - layout.x;
  if (dx < 0) return null;
  const slot = Math.floor(dx / pitch);
  if (slot >= layout.slots || dx - slot * pitch > layout.cell) return null;
  const dy = p.y - layout.y;
  for (const row of layout.rows) if (dy >= row.y && dy <= row.y + layout.cell) return { key: row.key, slot };
  for (const lane of layout.lanes) if (dy >= lane.y && dy <= lane.y + layout.cell) return { key: lane.key, slot, plane: lane.plane };
  return null;
};

/** A viewport point through the camera onto every published field. */
export const bitsUnder = (layouts: readonly FieldLayout[], camera: Pick<Camera, 'toStage'>, x: number, y: number): (BitHit & { world: string }) | null => {
  const p = camera.toStage(x, y);
  for (const layout of layouts) {
    const hit = bitAt(layout, p);
    if (hit) return { world: layout.world, ...hit };
  }
  return null;
};

/** Where a bit is, in stage units: the inverse of `bitAt`, for focus rings and origins. */
export const bitRect = (layout: FieldLayout, key: number, slot: number): { x: number; y: number; w: number; h: number } | null => {
  const row = layout.rows.find((r) => r.key === key);
  if (!row || slot < 0 || slot >= layout.slots) return null;
  return { x: layout.x + slot * (layout.cell + layout.gap), y: layout.y + row.y, w: layout.cell, h: layout.cell };
};

// ---------- the registry ----------

export interface SourceEntry { readonly el: HTMLElement; readonly held: () => Held | null; readonly region: Region }
export interface TargetEntry { readonly el: HTMLElement; readonly target: () => Target | null; readonly region: Region }
export interface VirtualTarget { readonly target: Target; readonly rect: DOMRect }

/** The little of a document the registry needs; tests pass a fake. */
export interface PointDoc { elementFromPoint(x: number, y: number): Element | null }

export interface Registry {
  addSource(el: HTMLElement, held: () => Held | null, region: Region): Unsub;
  addTarget(el: HTMLElement, target: () => Target | null, region: Region): Unsub;
  addResolver(fn: (x: number, y: number) => Target | null): Unsub;
  addVirtual(fn: () => readonly VirtualTarget[]): Unsub;
  sourceOf(el: Element | null): SourceEntry | null;
  targetOf(el: Element | null): TargetEntry | null;
  /**
   * The target under a viewport point. With `accept` (something is carried),
   * the first target out from the innermost that takes it: the field behind
   * its cells takes the Needle and the Lens. When none does, the innermost,
   * so the refusal names what was under the hand.
   */
  resolve(x: number, y: number, accept?: (t: Target) => boolean): Target | null;
  /** Every target now: DOM targets in document order, then virtual ones. */
  all(): Target[];
  /** The carry group a target's element belongs to (a field's cells), from `data-carry-group`. */
  groupOf(id: string): string | null;
  /** The target id that stands for a group on the keyboard: its roving tab stop. */
  leadOf(group: string): string | null;
  elementFor(id: string): HTMLElement | null;
  rectFor(id: string): DOMRect | null;
  sources(): readonly SourceEntry[];
  targets(): readonly TargetEntry[];
  regionOf(el: Element | null): Region | null;
  inRegion(region: Region): HTMLElement[];
}

const FOLLOWING = 4; // Node.DOCUMENT_POSITION_FOLLOWING, without needing a DOM at import

const byDocument = (a: HTMLElement, b: HTMLElement): number => {
  if (a === b || typeof a.compareDocumentPosition !== 'function') return 0;
  return a.compareDocumentPosition(b) & FOLLOWING ? -1 : 1;
};

const live = (el: HTMLElement): boolean => el.isConnected !== false;

/**
 * Laid out where a person can see it: not inside something hidden or inert
 * (a closed drawer), and with a box. The keyboard never carries to a place
 * nobody can see, nor sets focus it cannot take.
 */
const shown = (el: HTMLElement): boolean => {
  if (typeof el.closest === 'function' && el.closest('[hidden], [inert]')) return false;
  return typeof el.getClientRects !== 'function' || el.getClientRects().length > 0;
};

export const createRegistry = (doc: PointDoc | null = typeof document === 'undefined' ? null : document): Registry => {
  const sources = new Map<HTMLElement, SourceEntry>();
  const targets = new Map<HTMLElement, TargetEntry>();
  const resolvers: ((x: number, y: number) => Target | null)[] = [];
  const virtuals: (() => readonly VirtualTarget[])[] = [];
  const byId = new Map<string, HTMLElement>();
  const groups = new Map<string, string>();
  const leads = new Map<string, string>();
  const rects = new Map<string, DOMRect>();

  const closestIn = <E>(map: Map<HTMLElement, E>, el: Element | null, attr: string): E | null => {
    let at: Element | null = el;
    while (at) {
      const hit = map.get(at as HTMLElement);
      if (hit) return hit;
      at = at.parentElement?.closest(`[${attr}]`) ?? null;
    }
    return null;
  };

  const domTargets = (): { el: HTMLElement; target: Target }[] => {
    const out: { el: HTMLElement; target: Target }[] = [];
    const entries = [...targets.values()].filter((e) => live(e.el) && shown(e.el)).sort((a, b) => byDocument(a.el, b.el));
    groups.clear();
    leads.clear();
    for (const e of entries) {
      const t = e.target();
      if (!t) continue;
      byId.set(t.id, e.el);
      const group = typeof e.el.getAttribute === 'function' ? e.el.getAttribute('data-carry-group') : null;
      if (group) {
        groups.set(t.id, group);
        if (e.el.tabIndex === 0 || !leads.has(group)) leads.set(group, t.id);
      }
      out.push({ el: e.el, target: t });
    }
    return out;
  };

  const virtualTargets = (): Target[] => {
    const out: Target[] = [];
    for (const fn of virtuals) {
      for (const v of fn()) {
        rects.set(v.target.id, v.rect);
        out.push(v.target);
      }
    }
    return out;
  };

  const registry: Registry = {
    addSource(el, held, region) {
      const entry = { el, held, region };
      sources.set(el, entry);
      el.setAttribute('data-source', '');
      return () => {
        if (sources.get(el) === entry) {
          sources.delete(el);
          el.removeAttribute('data-source');
        }
      };
    },
    addTarget(el, target, region) {
      const entry = { el, target, region };
      targets.set(el, entry);
      el.setAttribute('data-target', '');
      return () => {
        if (targets.get(el) === entry) {
          targets.delete(el);
          el.removeAttribute('data-target');
        }
      };
    },
    addResolver(fn) {
      resolvers.push(fn);
      return () => {
        const i = resolvers.indexOf(fn);
        if (i >= 0) resolvers.splice(i, 1);
      };
    },
    addVirtual(fn) {
      virtuals.push(fn);
      return () => {
        const i = virtuals.indexOf(fn);
        if (i >= 0) virtuals.splice(i, 1);
      };
    },
    sourceOf: (el) => closestIn(sources, el, 'data-source'),
    targetOf: (el) => closestIn(targets, el, 'data-target'),
    resolve(x, y, accept) {
      let first: Target | null = null;
      // Out from the element under the point, through every registered target that holds it.
      let at: Element | null = doc?.elementFromPoint(x, y) ?? null;
      while (at) {
        const entry = targets.get(at as HTMLElement);
        const t = entry && live(entry.el) ? entry.target() : null;
        if (t) {
          byId.set(t.id, entry!.el);
          if (!accept || accept(t)) return t;
          first ??= t;
        }
        at = at.parentElement?.closest('[data-target]') ?? null;
      }
      for (const fn of resolvers) {
        const hit = fn(x, y);
        if (!hit) continue;
        if (!accept || accept(hit)) return hit;
        first ??= hit;
      }
      return first;
    },
    all: () => [...domTargets().map((d) => d.target), ...virtualTargets()],
    groupOf: (id) => groups.get(id) ?? null,
    leadOf: (group) => leads.get(group) ?? null,
    elementFor: (id) => {
      const el = byId.get(id);
      return el && live(el) ? el : null;
    },
    rectFor: (id) => rects.get(id) ?? null,
    sources: () => [...sources.values()],
    targets: () => [...targets.values()],
    regionOf: (el) => registry.sourceOf(el)?.region ?? registry.targetOf(el)?.region ?? null,
    inRegion(region) {
      const els = new Set<HTMLElement>();
      for (const e of sources.values()) if (e.region === region && live(e.el)) els.add(e.el);
      for (const e of targets.values()) if (e.region === region && live(e.el)) els.add(e.el);
      return [...els].sort(byDocument);
    },
  };
  return registry;
};

// ---------- scrub state, per World ----------

/** A World scrubbed back to `seq`: its matter then, and the bits the commit at the playhead set and cleared (the field pulses them). */
export interface Scrub {
  readonly seq: number;
  readonly matter: WorldMatter;
  readonly touched?: readonly { readonly key: number; readonly set: bigint; readonly clear: bigint }[];
}

const scrubbed = new Map<string, Scrub>();
const scrubWatchers = new Set<(world: string) => void>();

/**
 * Which Worlds are scrubbed back in time. The River scrubs; the field shows
 * the scrubbed matter; the gestures send a World back to Live before a write
 * lands on it.
 */
export const scrubs = {
  get: (world: string): Scrub | null => scrubbed.get(world) ?? null,
  seq: (world: string): number | null => scrubbed.get(world)?.seq ?? null,
  set(world: string, s: Scrub | null): void {
    const was = scrubbed.get(world) ?? null;
    if (s) scrubbed.set(world, s);
    else scrubbed.delete(world);
    if (was !== s) for (const fn of [...scrubWatchers]) fn(world);
  },
  live(world: string): void {
    scrubs.set(world, null);
  },
  /** What to draw for a World: its scrubbed matter, or the live one. */
  matter: (world: string, live: WorldMatter | undefined): WorldMatter | undefined => scrubbed.get(world)?.matter ?? live,
  on(fn: (world: string) => void): Unsub {
    scrubWatchers.add(fn);
    return () => scrubWatchers.delete(fn);
  },
};
